import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Check, Crown, Flame } from "lucide-react";

export default function Membership() {
  const { t } = useLang();
  const { user } = useAuth();
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState("");

  const { data: plans = [] } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });

  const checkout = async (lookupKey) => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/membership")}`;
      return;
    }
    setLoading(lookupKey);
    try {
      const res = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Checkout error");
      setLoading("");
    }
  };

  const tiers = [
    { tier: "member", icon: Flame, perks: t("member_perks"), highlight: false },
    { tier: "organizer", icon: Crown, perks: t("organizer_perks"), highlight: true },
  ];

  return (
    <>
      <Seo title={t("nav_membership")} description={t("membership_subtitle")} canonicalPath="/membership" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="text-center mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-3">swiss-pagan.ch</div>
          <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100 text-glow mb-4">{t("membership_title")}</h1>
          <p className="text-lg text-stone-400 max-w-2xl mx-auto">{t("membership_subtitle")}</p>

          <div className="inline-flex items-center gap-1 mt-8 bg-[#121418] border border-[#272A34] rounded-lg p-1">
            <button data-testid="interval-month" onClick={() => setInterval("month")} className={`px-5 py-2 rounded-md text-sm transition-all ${interval === "month" ? "bg-amber-600 text-stone-950 font-semibold" : "text-stone-300"}`}>{t("monthly")}</button>
            <button data-testid="interval-year" onClick={() => setInterval("year")} className={`px-5 py-2 rounded-md text-sm transition-all ${interval === "year" ? "bg-amber-600 text-stone-950 font-semibold" : "text-stone-300"}`}>{t("yearly")}</button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {tiers.map(({ tier, icon: Icon, perks, highlight }) => {
            const plan = plans.find((p) => p.tier === tier && p.interval === interval);
            const active = user?.subscription === tier;
            return (
              <div key={tier} data-testid={`plan-card-${tier}`} className={`relative rounded-2xl p-8 border transition-all ${highlight ? "border-amber-500/60 bg-gradient-to-b from-amber-950/20 to-[#121418] ember-glow" : "border-[#272A34] bg-[#121418]"}`}>
                {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider bg-amber-500 text-stone-950 font-bold">{t("most_popular")}</span>}
                <Icon className="w-10 h-10 text-amber-500 mb-4" />
                <h2 className="font-serif text-2xl text-amber-100">{plan?.name || (tier === "member" ? "Cercle Premium" : "Organisateur Pro")}</h2>
                <div className="mt-4 mb-6">
                  <span className="font-serif text-5xl text-stone-100">CHF {plan ? plan.amount : "—"}</span>
                  <span className="text-stone-500 text-sm ml-2">{interval === "month" ? t("billed_monthly") : t("billed_yearly")}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {perks.map((p, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-stone-300"><Check className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /> {p}</li>)}
                </ul>
                <button
                  data-testid={`subscribe-btn-${tier}`}
                  disabled={loading === plan?.lookup_key || active}
                  onClick={() => plan && checkout(plan.lookup_key)}
                  className={`w-full px-6 py-3 rounded-md font-semibold transition-all disabled:opacity-60 ${highlight ? "bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 border border-amber-400/30" : "bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/40"}`}
                >
                  {active ? "✓ Active" : loading === plan?.lookup_key ? "…" : t("subscribe")}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
